import React, { createContext, useContext, useState, useCallback } from 'react';
import ConfirmModal from './ConfirmModal';
import { SuccessIcon, ErrorIconFlat, WarningIcon, InfoIcon, CloseIcon } from './Icons';
import './Notification.css';

const NotificationContext = createContext();

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [confirmState, setConfirmState] = useState({ isOpen: false, message: '', title: '', type: 'warning', onResolve: null });

  const removeNotification = useCallback((id) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const showNotification = useCallback((message, type = 'info', duration = 5000) => {
    const id = Date.now() + Math.random();
    const notification = {
      id,
      message,
      type, // 'success', 'error', 'warning', 'info'
      duration,
    };

    setNotifications((prev) => [...prev, notification]);

    // Auto remove after duration
    if (duration > 0) {
      setTimeout(() => {
        removeNotification(id);
      }, duration);
    }

    return id;
  }, [removeNotification]);

  const success = useCallback((message, duration) => {
    return showNotification(message, 'success', duration);
  }, [showNotification]);

  const error = useCallback((message, duration) => {
    return showNotification(message, 'error', duration);
  }, [showNotification]);

  const warning = useCallback((message, duration) => {
    return showNotification(message, 'warning', duration);
  }, [showNotification]);

  const info = useCallback((message, duration) => {
    return showNotification(message, 'info', duration);
  }, [showNotification]);

  const showConfirm = useCallback((message, title = 'Patvirtinti', type = 'warning') => {
    return new Promise((resolve) => {
      setConfirmState({
        isOpen: true,
        message,
        title,
        type,
        onResolve: resolve,
      });
    });
  }, []);

  const handleConfirm = useCallback((result) => {
    if (confirmState.onResolve) {
      confirmState.onResolve(result);
    }
    setConfirmState({ isOpen: false, message: '', title: '', type: 'warning', onResolve: null });
  }, [confirmState]);

  return (
    <NotificationContext.Provider
      value={{
        showNotification,
        success,
        error,
        warning,
        info,
        removeNotification,
        showConfirm,
      }}
    >
      {children}
      <NotificationContainer notifications={notifications} onRemove={removeNotification} />
      <ConfirmModal
        isOpen={confirmState.isOpen}
        title={confirmState.title}
        message={confirmState.message}
        type={confirmState.type}
        onConfirm={() => handleConfirm(true)}
        onCancel={() => handleConfirm(false)}
      />
    </NotificationContext.Provider>
  );
};

function NotificationContainer({ notifications, onRemove }) {
  return (
    <div className="notification-container">
      {notifications.map((notification) => (
        <NotificationItem
          key={notification.id}
          notification={notification}
          onRemove={onRemove}
        />
      ))}
    </div>
  );
}

function NotificationItem({ notification, onRemove }) {
  const { id, message, type } = notification;

  const getIcon = () => {
    switch (type) {
      case 'success':
        return <SuccessIcon />;
      case 'error':
        return <ErrorIconFlat />;
      case 'warning':
        return <WarningIcon />;
      case 'info':
        return <InfoIcon />;
      default:
        return <InfoIcon />;
    }
  };

  return (
    <div
      className={`notification notification-${type}`}
      onClick={() => onRemove(id)}
    >
      <div className="notification-icon">{getIcon()}</div>
      <div className="notification-content">
        <p className="notification-message">{message}</p>
      </div>
      <button
        className="notification-close"
        onClick={(e) => {
          e.stopPropagation();
          onRemove(id);
        }}
        aria-label="Uždaryti"
      >
        <CloseIcon />
      </button>
    </div>
  );
}

export default NotificationProvider;
